"use client";

import { Check, Circle } from "lucide-react";

import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { PASSWORD_RULES, checkPasswordRules } from "@/lib/validation/auth";

/**
 * Inline validation message under an auth/account field.
 *
 * `role="alert"` so screen readers announce it, and the field itself should
 * carry `aria-invalid` + `aria-describedby={`${id}-error`}`.
 */
export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p
      id={`${id}-error`}
      role="alert"
      style={{
        fontFamily: CORE_FONT.sans,
        fontSize: 13,
        lineHeight: "18px",
        color: CORE_COLORS.brandRed,
      }}
    >
      {message}
    </p>
  );
}

/**
 * Live password-requirement checklist.
 *
 * QA asked for the rules to be visible up front, not just as an error after a
 * failed submit: "密码validation需要做，另外密码提示例如8个字，需要大小写，英文
 * 数字等等". Rendered from the same PASSWORD_RULES the schema validates against,
 * so the hint and the rule can never drift apart.
 */
export function PasswordHints({ value }: { value: string }) {
  const state = checkPasswordRules(value);
  return (
    <ul className="flex flex-col gap-[4px]" aria-live="polite">
      {PASSWORD_RULES.map((rule) => {
        const met = state[rule.id];
        return (
          <li
            key={rule.id}
            className="flex items-center gap-[6px]"
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: 12,
              lineHeight: "16px",
              color: met ? CORE_COLORS.brandGreenDark : CORE_COLORS.textSecondary,
            }}
          >
            {/* Icons rather than the "✓" / "•" glyphs this used to print: the
                bullet and the tick came from different parts of the font, so
                they sat at different weights and baselines and the list
                jittered as rules were met. */}
            {met ? (
              <Check
                aria-hidden="true"
                className="size-[14px] shrink-0"
                strokeWidth={3}
                color={CORE_COLORS.brandGreenDark}
              />
            ) : (
              <Circle
                aria-hidden="true"
                className="size-[14px] shrink-0"
                strokeWidth={2}
                color={CORE_COLORS.textTertiary}
              />
            )}
            <span>{rule.label}</span>
            <span className="sr-only">{met ? "（已符合）" : "（未符合）"}</span>
          </li>
        );
      })}
    </ul>
  );
}
